import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
// Import Types
import type { MenuItemDropdownRef } from 'components/MenuItemDropdown/types';
// Import Store
import { useAppSelector } from 'store';
import { selectPageValidation } from 'store/slices/pageValidation';
//Import Constants
import { l } from 'constants/common';
import { EmailRecipientStatusColors, EmilRecipientStatus } from './config';
// Import Enums
import { MenuItemIdEnum } from 'enums';
// Import UI Components
import { Dropdown, Icon, Tag, Tooltip } from '@geneui/components';
// Import Components
import MenuItemDropdown from 'components/MenuItemDropdown';
import RandomComponentWithHeadline from 'components/common/RandomComponentWithHeadline';
// Import Styles
import styles from './scheduler.module.scss';

const mapToValues = (list) => {
    return list?.map(({ value }) => value);
};

const RecipientSelectors = (props) => {
    const {
        defaultEmailRecipientsList,
        onIsLoadingEmailRecipientsChange,
        emailRecipientsList,
        setEmailRecipientsList,
        selectedEmailRecipients,
        setSelectedEmailRecipients,
        ftpRecipientsList,
        selectedFtpRecipients,
        setSelectedFtpRecipients,
        reportType,
    } = props;
    const { t } = useTranslation();

    const pageValidation = useAppSelector(selectPageValidation);

    const [selectedFtpRecipientsValues, setSelectedFtpRecipientsValues] = useState([]);

    const menuItemDropdownRef = useRef<MenuItemDropdownRef>(null);

    const deleteChosenRecipients = (e) => {
        const filteredSelectedValues = selectedEmailRecipients.filter((val) => +val !== +e);
        setSelectedEmailRecipients(filteredSelectedValues);
        menuItemDropdownRef.current?.updateValue?.(filteredSelectedValues);
    };

    const deleteChosenFtp = (e) => {
        const filteredSelectedValues = selectedFtpRecipients.filter((val) => +val.value !== +e);
        setSelectedFtpRecipients(filteredSelectedValues);
    };

    useEffect(() => {
        setSelectedFtpRecipientsValues(mapToValues(selectedFtpRecipients));
    }, [selectedFtpRecipients]);

    const getUpdate = useCallback(
        (dropdownValues) => {
            if ('value' in dropdownValues) {
                setSelectedEmailRecipients(dropdownValues.value);
            }
        },
        [setSelectedEmailRecipients],
    );

    const emailRecipientsDefaultValues = useMemo(() => {
        return defaultEmailRecipientsList?.map(({ UserId }) => `${UserId}`);
    }, [defaultEmailRecipientsList]);

    useEffect(() => {
        menuItemDropdownRef.current?.updateValue?.(emailRecipientsDefaultValues);
        menuItemDropdownRef.current?.search?.({});
    }, [emailRecipientsDefaultValues]);

    const emailRecipientsAdditionalParams = useMemo(() => {
        return {
            reportType,
            ides: defaultEmailRecipientsList?.map(({ UserId }) => UserId),
        };
    }, [defaultEmailRecipientsList, reportType]);

    const selectedEmailRecipientsOptions = useMemo(() => {
        return selectedEmailRecipients
            ?.map((recipientsId) => emailRecipientsList.find(({ Id }) => +Id === +recipientsId))
            .filter(Boolean)
            .map(({ Id, Name, Email, State, StateName }) => ({
                label: State === EmilRecipientStatus.Deleted ? `${Id}` : `${Name} (${Email})`,
                value: `${Id}`,
                State,
                StateName,
            }));
    }, [emailRecipientsList, selectedEmailRecipients]);

    return (
        <div className="providers-wrap">
            <div className="providers-wrap-inner">
                <div className="title-wrapper">
                    <p className="title-wrapper-text">{t(l.EmailRecipients)}</p>
                    <span className="title-wrapper-line" />
                </div>
                <div className="providers-wrap-row">
                    <MenuItemDropdown
                        ref={menuItemDropdownRef}
                        displayName={t(l.Name)}
                        menuItemId={MenuItemIdEnum.RecipientsWithAccessToPartner}
                        placeholder={t(l.Select)}
                        className={styles.menuItemDropdown}
                        doInitialRequest={false}
                        isMultiselect={true}
                        extendTargetWidth={false}
                        additionalParams={emailRecipientsAdditionalParams}
                        onIsLoadingChange={onIsLoadingEmailRecipientsChange}
                        getUpdate={getUpdate}
                        getDropdownData={setEmailRecipientsList}
                        popoverAlign="start"
                        popoverClassName={styles.popover}
                        isValid={pageValidation?.EmailRecipients?.isValid}
                        errorText={pageValidation?.EmailRecipients?.errorText}
                    />
                    <RandomComponentWithHeadline className={'crm-form-single-input-equal'}>
                        <div className={'crm-multi-select-country-section-body'}>
                            <div className="crm-multi-select-country-inner">
                                <p>{t(l.SelectedItems)}</p>
                                {selectedEmailRecipientsOptions &&
                                    selectedEmailRecipientsOptions.map(({ label, value, State, StateName }) => (
                                        <Tooltip key={value} text={StateName}>
                                            <Tag
                                                name={label}
                                                appearance="light"
                                                cornerRadius="smooth-radius"
                                                className={'crm-multi-select-country-section__tag'}
                                                color={EmailRecipientStatusColors[State]}
                                                icons={
                                                    <Icon
                                                        type={'bc-icon-clear'}
                                                        onClick={() => deleteChosenRecipients(value)}
                                                    />
                                                }
                                            />
                                        </Tooltip>
                                    ))}
                            </div>
                        </div>
                    </RandomComponentWithHeadline>
                </div>
            </div>
            <div className="providers-wrap-inner">
                <div className="title-wrapper">
                    <p className="title-wrapper-text">{t(l.FTPRecipients)}</p>
                    <span className="title-wrapper-line" />
                </div>
                <div className="providers-wrap-row">
                    <Dropdown
                        data={ftpRecipientsList}
                        value={selectedFtpRecipientsValues}
                        isMultiSelect={true}
                        onChange={(values) => setSelectedFtpRecipients(values)}
                    />
                    <RandomComponentWithHeadline className={'crm-form-single-input-equal'}>
                        <div className={'crm-multi-select-country-section-body'}>
                            <div className="crm-multi-select-country-inner">
                                <p>{t(l.SelectedItems)}</p>
                                {selectedFtpRecipients &&
                                    selectedFtpRecipients.map(({ label, value }) => (
                                        <Tag
                                            name={label}
                                            key={value}
                                            appearance="minimal"
                                            cornerRadius="smooth-radius"
                                            className={'crm-multi-select-country-section__tag'}
                                            icons={
                                                <Icon type={'bc-icon-clear'} onClick={() => deleteChosenFtp(value)} />
                                            }
                                        />
                                    ))}
                            </div>
                        </div>
                    </RandomComponentWithHeadline>
                </div>
            </div>
        </div>
    );
};

RecipientSelectors.propTypes = {
    defaultEmailRecipientsList: PropTypes.array,
    onIsLoadingEmailRecipientsChange: PropTypes.func,
    emailRecipientsList: PropTypes.array,
    setEmailRecipientsList: PropTypes.func,
    selectedEmailRecipients: PropTypes.array,
    setSelectedEmailRecipients: PropTypes.func,
    ftpRecipientsList: PropTypes.array,
    selectedFtpRecipients: PropTypes.array,
    setSelectedFtpRecipients: PropTypes.func,
    reportType: PropTypes.number,
};

RecipientSelectors.defaultProps = { selectedEmailRecipients: [], selectedFtpRecipients: [] };

export default memo(RecipientSelectors);
